import type { SajuResult, Element } from './sajuCalculations';
import { STEM_ELEMENTS, BRANCH_ELEMENTS } from './sajuCalculations';

// ===== 합/충 기본 데이터 =====
const STEM_HARMONY_PAIRS = [[0,5],[1,6],[2,7],[3,8],[4,9]]; // 천간합
const STEM_CLASH_PAIRS   = [[0,6],[1,7],[2,8],[3,9]];       // 천간충
const BRANCH_SIX_HARMONY = [[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]]; // 지지육합
const BRANCH_CLASH_PAIRS = [[0,6],[1,7],[2,8],[3,9],[4,10],[5,11]]; // 지지충
const BRANCH_THREE_GROUPS = [[8,0,4],[2,6,10],[5,9,1],[11,3,7]];   // 지지삼합

function pairMatch(pairs: number[][], a: number, b: number): boolean {
  return pairs.some(([x,y]) => (a===x&&b===y)||(a===y&&b===x));
}
function groupContains(groups: number[][], a: number, b: number): boolean {
  return groups.some(g => g.includes(a) && g.includes(b));
}

// ===== 오행 분포 계산 =====
export function getElementDistribution(saju: SajuResult): Record<Element, number> {
  const dist: Record<Element, number> = { 목:0, 화:0, 토:0, 금:0, 수:0 };
  const pillars = [saju.yearPillar, saju.monthPillar, saju.dayPillar, saju.hourPillar].filter(Boolean);
  for (const p of pillars) {
    if (!p) continue;
    dist[STEM_ELEMENTS[p.stemIdx]]++;
    dist[BRANCH_ELEMENTS[p.branchIdx]]++;
  }
  return dist;
}

// 오행 보완성 (0-100)
function calcElementComplementarity(distA: Record<Element, number>, distB: Record<Element, number>): number {
  const elements: Element[] = ['목','화','토','금','수'];
  const totalA = Object.values(distA).reduce((s,v) => s+v, 0);
  const totalB = Object.values(distB).reduce((s,v) => s+v, 0);
  if (!totalA || !totalB) return 50;

  let score = 50;
  for (const el of elements) {
    const ratioA = distA[el] / totalA;
    const ratioB = distB[el] / totalB;
    const deficit = Math.max(0, 0.2 - ratioA); // A에 부족한 정도
    const surplus = Math.max(0, ratioB - 0.2); // B에 넘치는 정도
    score += deficit * surplus * 200; // 부족-충족 매칭 보너스
  }
  return Math.min(100, Math.max(0, Math.round(score)));
}

// ===== 궁합 점수 =====
export interface GoonghapScores {
  personality: number;    // 성향
  communication: number;  // 소통
  wealth: number;         // 재물
  affection: number;      // 애정
  timing: number;         // 타이밍
  total: number;
}

export function calculateGoonghapScores(saju1: SajuResult, saju2: SajuResult): GoonghapScores {
  const p1 = saju1.dayPillar;
  const p2 = saju2.dayPillar;

  // ─── 성향 (일간/일지 관계) ───
  let personality = 55;
  if (pairMatch(STEM_HARMONY_PAIRS, p1.stemIdx, p2.stemIdx))   personality += 28;
  if (pairMatch(STEM_CLASH_PAIRS,   p1.stemIdx, p2.stemIdx))   personality -= 22;
  if (pairMatch(BRANCH_SIX_HARMONY, p1.branchIdx, p2.branchIdx)) personality += 20;
  if (groupContains(BRANCH_THREE_GROUPS, p1.branchIdx, p2.branchIdx)) personality += 12;
  if (pairMatch(BRANCH_CLASH_PAIRS,  p1.branchIdx, p2.branchIdx))  personality -= 18;
  personality = clamp(personality, 10, 98);

  // ─── 소통 (전체 기둥 합/충 카운트) ───
  let communication = 60;
  const pillars1 = [saju1.yearPillar, saju1.monthPillar, saju1.dayPillar, saju1.hourPillar].filter(Boolean);
  const pillars2 = [saju2.yearPillar, saju2.monthPillar, saju2.dayPillar, saju2.hourPillar].filter(Boolean);
  for (const a of pillars1) {
    for (const b of pillars2) {
      if (!a || !b) continue;
      if (pairMatch(STEM_CLASH_PAIRS,   a.stemIdx,   b.stemIdx))   communication -= 9;
      if (pairMatch(BRANCH_CLASH_PAIRS, a.branchIdx, b.branchIdx)) communication -= 8;
      if (pairMatch(STEM_HARMONY_PAIRS, a.stemIdx,   b.stemIdx))   communication += 7;
      if (pairMatch(BRANCH_SIX_HARMONY, a.branchIdx, b.branchIdx)) communication += 6;
    }
  }
  // 월지 충 패널티
  if (pairMatch(BRANCH_CLASH_PAIRS, saju1.monthPillar.branchIdx, saju2.monthPillar.branchIdx)) {
    communication -= 12;
  }
  communication = clamp(communication, 10, 98);

  // ─── 재물 (년주 관계 + 금/토 오행) ───
  let wealth = 50;
  const y1 = saju1.yearPillar;
  const y2 = saju2.yearPillar;
  if (pairMatch(STEM_HARMONY_PAIRS, y1.stemIdx, y2.stemIdx))   wealth += 25;
  if (pairMatch(BRANCH_SIX_HARMONY, y1.branchIdx, y2.branchIdx)) wealth += 18;
  if (pairMatch(BRANCH_CLASH_PAIRS, y1.branchIdx, y2.branchIdx)) wealth -= 18;
  if (groupContains(BRANCH_THREE_GROUPS, y1.branchIdx, y2.branchIdx)) wealth += 12;

  const dist1 = getElementDistribution(saju1);
  const dist2 = getElementDistribution(saju2);
  const combined금 = dist1['금'] + dist2['금'];
  const combined토 = dist1['토'] + dist2['토'];
  if (combined금 >= 2) wealth += 10;
  if (combined토 >= 2) wealth += 8;
  wealth = clamp(wealth, 10, 98);

  // ─── 애정 (오행 보완성 + 이성 반응) ───
  let affection = calcElementComplementarity(dist1, dist2);
  if (pairMatch(BRANCH_CLASH_PAIRS, p1.branchIdx, p2.branchIdx) &&
      [0,6,2,8].includes(p1.branchIdx)) {
    affection += 12; // 자오충/인신충 - 이끌림
  }
  if (pairMatch(BRANCH_SIX_HARMONY, p1.branchIdx, p2.branchIdx)) affection += 15;
  affection = clamp(affection, 10, 98);

  // ─── 타이밍 (현재 연도 기운) ───
  const currentYear = new Date().getFullYear();
  const yearBranch = ((currentYear - 4) % 12 + 12) % 12;
  let timing = 55;
  if (groupContains(BRANCH_THREE_GROUPS, p1.branchIdx, yearBranch)) timing += 18;
  if (groupContains(BRANCH_THREE_GROUPS, p2.branchIdx, yearBranch)) timing += 18;
  if (pairMatch(BRANCH_CLASH_PAIRS, p1.branchIdx, yearBranch)) timing -= 12;
  if (pairMatch(BRANCH_CLASH_PAIRS, p2.branchIdx, yearBranch)) timing -= 12;
  // 두 사람의 일지 유사한 운세 흐름 보너스
  if (groupContains(BRANCH_THREE_GROUPS, p1.branchIdx, p2.branchIdx)) timing += 10;
  timing = clamp(timing, 20, 95);

  const total = Math.round(
    personality * 0.25 +
    communication * 0.20 +
    wealth * 0.20 +
    affection * 0.25 +
    timing * 0.10
  );

  return { personality, communication, wealth, affection, timing, total };
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(v)));
}

// ===== 조언 생성 =====
export interface GoonghapAdvice {
  category: string;
  icon: string;
  score: number;
  title: string;
  advice: string;
  tip?: string;
}

const ADVICE_TABLE: Record<string, { high: { title: string; advice: string; tip?: string }; mid: { title: string; advice: string; tip?: string }; low: { title: string; advice: string; tip?: string } }> = {
  personality: {
    high: { title: '천생연분 성향', advice: '두 분은 본질적인 성격이 잘 맞아요. 함께 있으면 자연스럽고 편안한 느낌이 들 거예요.', tip: '첫 만남부터 이상하게 편한 그 느낌, 맞아요 😊' },
    mid:  { title: '개성 있는 한 쌍', advice: '서로 비슷한 점도, 다른 점도 있는 궁합이에요. 다름을 개성으로 인정하면 더 재밌는 관계가 될 수 있어요.' },
    low:  { title: '정반대의 두 사람', advice: '두 분의 기본 성향이 꽤 달라요.', tip: '단, "우리는 달라서 싸운다"가 아니라 "달라서 배울 게 많다"로 프레임을 바꿔보세요. 정반대 성향이 최강의 팀이 되기도 하거든요.' },
  },
  communication: {
    high: { title: '최고의 말이 통하는 사이', advice: '싸워도 대화가 통하는 궁합이에요. 갈등이 생겨도 대화로 대부분 해결할 수 있어요.' },
    mid:  { title: '조금 다른 대화 방식', advice: '가끔 서로 다른 주파수를 쓰는 것 같은 느낌이 들 수 있어요.', tip: '감정이 격해졌을 때는 "잠깐 쉬었다 얘기하자"고 먼저 말하는 쪽이 이기는 거예요 🤫' },
    low:  { title: '번역기가 필요한 소통', advice: '두 분은 갈등이 생겼을 때 대화 방식이 상당히 달라요.', tip: '싸울 때 과거 이야기 절대 금지. 이 궁합은 한 번 선을 넘으면 수습이 까다로워요. 주 2회 데이트, 각자 공간 확보 추천!' },
  },
  wealth: {
    high: { title: '황금 재물 시너지', advice: '두 분이 합쳤을 때 재물 흐름이 좋아져요. 같이 재테크나 목표를 세워보는 것도 좋아요.', tip: '함께 적금 통장 하나 만드는 걸 추천해요 💸' },
    mid:  { title: '평범한 재물 흐름', advice: '재물 면에서 큰 시너지나 큰 충돌 없이 무난하게 흘러가는 궁합이에요.' },
    low:  { title: '경제권 각자 관리 권장', advice: '두 분이 함께 투자하거나 사업을 하면 손해 볼 가능성이 있어요.', tip: '경제권은 철저히 각자 관리하고, 공동 지출만 따로 통장을 만드세요. 같이 주식·코인은 절대 하지 마세요 😅' },
  },
  affection: {
    high: { title: '서로의 빈틈을 채우는 사이', advice: '서로에게 필요한 기운을 정확히 채워주고 있어요. 함께 있으면 심리적으로 안정되고 힘이 나는 궁합이에요.', tip: 'A님의 부족한 기운을 B님이 딱 채워주고 있어요 🧩' },
    mid:  { title: '균형 잡힌 관계', advice: '오행의 균형이 나쁘지 않아요. 서로를 보완해 주는 부분도 있고, 충분히 매력을 느낄 수 있는 궁합이에요.' },
    low:  { title: '채워주기보다 채워야 하는', advice: '두 분이 비슷한 기운을 가지고 있어서, 서로의 부족함을 채워주기보다 같이 채워야 하는 관계예요.', tip: '억지로 끼워 맞추려 하지 마세요. 함께 성장하는 방향으로 접근하면 오히려 좋아요 💪' },
  },
  timing: {
    high: { title: '지금 이 순간이 최고의 타이밍', advice: '올해 두 분의 운세 흐름이 비슷한 방향을 가리키고 있어요. 지금 만나기 정말 좋은 시기예요.' },
    mid:  { title: '보통의 타이밍', advice: '지금 시기가 두 분에게 나쁘지 않아요. 크게 좋지도 나쁘지도 않은 무난한 때에요.' },
    low:  { title: '시간이 약이에요', advice: '현재 두 분의 운세 사이클이 조금 엇박자예요.', tip: '지금 당장 큰 결정(이사, 결혼, 동업 등)보다는 천천히 알아가는 시간을 갖는 게 좋아요 ⏳' },
  },
};

export function generateAdvice(scores: GoonghapScores): GoonghapAdvice[] {
  const categoryMap: { key: keyof GoonghapScores; label: string; icon: string }[] = [
    { key: 'personality',    label: '성향',  icon: '🔥' },
    { key: 'communication',  label: '소통',  icon: '💬' },
    { key: 'wealth',         label: '재물',  icon: '💰' },
    { key: 'affection',      label: '애정',  icon: '💖' },
    { key: 'timing',         label: '타이밍', icon: '⏳' },
  ];
  return categoryMap.map(({ key, label, icon }) => {
    const score = scores[key] as number;
    const table = ADVICE_TABLE[key];
    const entry = score >= 68 ? table.high : score >= 40 ? table.mid : table.low;
    return { category: label, icon, score, title: entry.title, advice: entry.advice, tip: entry.tip };
  });
}

// 총점 기반 요약 메시지
export function getSummaryMessage(total: number, nameA: string, nameB: string): { headline: string; sub: string; emoji: string; isFakPok: boolean } {
  if (total >= 80) return {
    headline: `${nameA}님과 ${nameB}님의 바른 궁합 점수는 ${total}점!`,
    sub: '서로의 빈틈을 완벽하게 채워주는 퍼즐 같은 사이예요 🧩',
    emoji: '💝', isFakPok: false,
  };
  if (total >= 65) return {
    headline: `${nameA}님과 ${nameB}님의 바른 궁합 점수는 ${total}점!`,
    sub: '기반은 탄탄해요. 서로를 이해하는 노력이 더해지면 금상첨화예요 ✨',
    emoji: '💫', isFakPok: false,
  };
  if (total >= 50) return {
    headline: `${nameA}님과 ${nameB}님의 바른 궁합 점수는 ${total}점!`,
    sub: '보통 궁합이에요. 노력에 따라 얼마든지 달라질 수 있는 관계예요 🌱',
    emoji: '🌿', isFakPok: false,
  };
  if (total >= 40) return {
    headline: `이 관계, 각오가 필요합니다. ${total}점`,
    sub: '쉽지는 않지만, 이 궁합을 이해하면 오히려 강한 한 쌍이 될 수 있어요 💪',
    emoji: '⚡', isFakPok: true,
  };
  return {
    headline: `이 관계, 사랑만으로 극복하기엔 난이도 '최상'입니다. ${total}점`,
    sub: '두 분은 퍼즐의 짝이 아닙니다. 억지로 끼워 맞추면 서로 모서리만 닳아요 💔',
    emoji: '🚨', isFakPok: true,
  };
}
